/**
 * Toast Component
 * 
 * Simple toast notification system
 */

import { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastData {
    id: string;
    type: ToastType;
    message: string;
    duration?: number;
}

interface ToastProps {
    toast: ToastData;
    onRemove: (id: string) => void;
}

function Toast({ toast, onRemove }: ToastProps) {
    useEffect(() => {
        const timer = setTimeout(() => {
            onRemove(toast.id);
        }, toast.duration || 3000);

        return () => clearTimeout(timer);
    }, [toast.id, toast.duration, onRemove]);

    const icons = {
        success: <CheckCircle className="h-5 w-5 text-green-500" />,
        error: <AlertCircle className="h-5 w-5 text-red-500" />,
        info: <Info className="h-5 w-5 text-blue-500" />,
    };

    const bgColors = {
        success: 'bg-green-50 border-green-200',
        error: 'bg-red-50 border-red-200',
        info: 'bg-blue-50 border-blue-200',
    };

    return (
        <div
            className={`flex items-center gap-3 rounded-lg border p-4 shadow-lg ${bgColors[toast.type]} animate-in slide-in-from-right-full`}
        >
            {icons[toast.type]}
            <span className="flex-1 text-sm text-gray-800">{toast.message}</span>
            <button
                onClick={() => onRemove(toast.id)}
                className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    );
}

// Toast container
let toastListeners: ((toasts: ToastData[]) => void)[] = [];
let toasts: ToastData[] = [];

function notifyListeners() {
    toastListeners.forEach(listener => listener([...toasts]));
}

export function toast(type: ToastType, message: string, duration = 3000) {
    const id = crypto.randomUUID();
    toasts = [...toasts, { id, type, message, duration }];
    notifyListeners();
    return id;
}

toast.success = (message: string, duration?: number) => toast('success', message, duration);
toast.error = (message: string, duration?: number) => toast('error', message, duration);
toast.info = (message: string, duration?: number) => toast('info', message, duration);

export function ToastContainer() {
    const [localToasts, setLocalToasts] = useState<ToastData[]>([]);

    useEffect(() => {
        const listener = (newToasts: ToastData[]) => setLocalToasts(newToasts);
        toastListeners.push(listener);
        return () => {
            toastListeners = toastListeners.filter(l => l !== listener);
        };
    }, []);

    const handleRemove = (id: string) => {
        toasts = toasts.filter(t => t.id !== id);
        notifyListeners();
    };

    if (localToasts.length === 0) return null;

    return (
        <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
            {localToasts.map(t => (
                <Toast key={t.id} toast={t} onRemove={handleRemove} />
            ))}
        </div>
    );
}
